import React, { useState, useEffect } from 'react';
import { getPillar } from '../pillars';

/**
 * CommunityExamples - Lived knowledge from learners
 *
 * After completing a level, learners can share:
 * "An example from my life where this thinking showed up"
 *
 * Design principles:
 * - Short (max 280 characters)
 * - Anonymous by default
 * - Moderated only for harm (not implemented yet)
 * - Tagged by pillar + level
 * - No likes, no comments, no rankings
 * - Just presence
 * - Collapsed by default
 */
function CommunityExamples({ levelId, pillarKey }) {
  const [examples, setExamples] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const pillar = pillarKey ? getPillar(pillarKey) : null;

  useEffect(() => {
    // Load community examples from localStorage
    const storedExamples = localStorage.getItem('sifiso-community-examples');
    if (storedExamples) {
      const allExamples = JSON.parse(storedExamples);
      // Filter examples for this level
      const levelExamples = allExamples.filter(ex => ex.levelId === levelId);
      setExamples(levelExamples);
    }
  }, [levelId]);

  if (examples.length === 0) {
    return null; // Don't show section if no examples exist
  }

  return (
    <div className="mt-6 p-4 bg-slate-900/30 rounded-lg border border-slate-700/30">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm font-medium">
            Community Examples
          </span>
          <span className="text-slate-500 text-xs">
            ({examples.length})
          </span>
        </div>
        <span className="text-slate-500 text-sm">
          {isExpanded ? '▼' : '▶'}
        </span>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className="text-slate-500 text-xs italic mb-3">
            From people learning alongside you
          </p>

          {examples.map((example, idx) => {
            const borderColorClass = pillar
              ? `border-l-${pillar.color}-500`
              : 'border-l-slate-500';
            return (
            <div
              key={idx}
              className={`p-3 bg-slate-800/50 rounded-lg border-l-2 ${borderColorClass}`}
            >
              <p className="text-slate-300 text-sm leading-relaxed">
                "{example.text}"
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                {pillar && (
                  <>
                    <span>{pillar.icon}</span>
                    <span>{pillar.shortName}</span>
                  </>
                )}
                <span>•</span>
                <span>
                  {new Date(example.timestamp).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CommunityExamples;
